import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ITripGroup } from '../types/trip.types';
import { ILineItemUpdate } from '../types/verify-travel.types';
import { buildLineItemUpdates } from '../services/lineItem.service';

interface UseTripApprovalProps {
    approveTrip: (tripId: number, requestId: number, requiresEmail: boolean, lineItemUpdates?: ILineItemUpdate[]) => Promise<any>;
    declineTrip: (tripId: number, requestId: number, requiresEmail: boolean, lineItemUpdates?: ILineItemUpdate[]) => Promise<any>;
    onTripsReload: () => Promise<ITripGroup[] | void>;
    lineItemState: Map<number, Partial<any>>;
    reviewRequiredState: Map<number, boolean>;
    onCloseSidebar?: () => void;
    onAfterApprovalOrDecline?: (tripIndex?: number, freshTripGroups?: ITripGroup[]) => void;
}

export const useTripApproval = (props: UseTripApprovalProps) => {
    const [isApprovingTrip, setIsApprovingTrip] = useState<boolean>(false);
    const [isDecliningTrip, setIsDecliningTrip] = useState<boolean>(false);

    const approveTrip = useCallback(async (tripId: number, requestId: number, tripGroup: ITripGroup, tripIndex?: number) => {
        const lineItemUpdates = tripGroup && tripGroup.lineItems.length > 0 
            ? buildLineItemUpdates(tripGroup.lineItems, props.lineItemState, props.reviewRequiredState)
            : [];
        
        setIsApprovingTrip(true);
        try {
            await props.approveTrip(tripId, requestId, false, lineItemUpdates);
            toast.success('Trip approved successfully');
            
            if (props.onCloseSidebar) {
                props.onCloseSidebar();
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            const reloadResult = await props.onTripsReload();
            const freshTripGroups = Array.isArray(reloadResult) ? reloadResult : undefined;
            
            if (props.onAfterApprovalOrDecline) {
                props.onAfterApprovalOrDecline(tripIndex, freshTripGroups);
            }
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || err?.message || 'An error occurred while approving the trip.';
            toast.error(errorMessage);
        } finally {
            setIsApprovingTrip(false);
        }
    }, [props]);

    const declineTrip = useCallback(async (tripId: number, requestId: number, tripGroup: ITripGroup, tripIndex?: number) => {
        const lineItemUpdates = tripGroup && tripGroup.lineItems.length > 0 
            ? buildLineItemUpdates(tripGroup.lineItems, props.lineItemState, props.reviewRequiredState)
            : [];
        
        setIsDecliningTrip(true);
        try {
            await props.declineTrip(tripId, requestId, false, lineItemUpdates);
            toast.success('Trip declined successfully');
            
            if (props.onCloseSidebar) {
                props.onCloseSidebar();
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            const reloadResult = await props.onTripsReload();
            const freshTripGroups = Array.isArray(reloadResult) ? reloadResult : undefined;
            
            if (props.onAfterApprovalOrDecline) {
                props.onAfterApprovalOrDecline(tripIndex, freshTripGroups);
            }
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || err?.message || 'An error occurred while declining the trip.';
            toast.error(errorMessage);
        } finally {
            setIsDecliningTrip(false);
        }
    }, [props]);

    return {
        isApprovingTrip,
        isDecliningTrip,
        approveTrip,
        declineTrip
    };
};

